/**
 * ChurchScreenPage — the calendar on a screen at the back of the hall.
 *
 * A smart TV browser opens `/church/:slug/screen` and leaves it there: this
 * week's services large enough to read across a room, the subscribe QR in a
 * corner so anyone can take the calendar home, and a quiet refresh every ten
 * minutes so a service added on Thursday is on the screen by Friday.
 *
 * Nothing here is not already on the public calendar — no login, no
 * navigation, no chrome.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'react-qr-code';
import { isoDay } from '@/lib/localDate';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 10 * 60 * 1000;

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const weekWindow = () => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - now.getDay());      // Sunday
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: isoDay(start), end: isoDay(end) };
};

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(tick);
  }, []);
  return (
    <div className="text-right" data-testid="screen-clock">
      <p className="text-5xl font-semibold tabular-nums tracking-tight">
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </p>
      <p className="text-lg text-white/60">
        {now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>
    </div>
  );
};

export default function ChurchScreenPage() {
  const { slug } = useParams();
  const { start, end } = weekWindow();

  const { data, refetch } = useQuery({
    queryKey: ['church-screen', slug, start],
    queryFn: async () => {
      const r = await fetch(
        `${API_URL}/api/public/church-calendar/${slug}?start=${start}&end=${end}`);
      if (!r.ok) throw new Error('This calendar is not published');
      return r.json();
    },
    refetchInterval: REFRESH_MS,
  });

  const { data: subscribe } = useQuery({
    queryKey: ['church-screen-subscribe', slug],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/public/church-calendar/${slug}/subscribe`);
      if (!r.ok) throw new Error('no link');
      return r.json();
    },
    staleTime: 3_600_000,
  });

  useEffect(() => {
    const beat = setInterval(() => refetch(), REFRESH_MS);
    return () => clearInterval(beat);
  }, [refetch]);

  const today = isoDay(new Date());
  const items = (data?.items || []).filter((i) => i.date >= today);
  const grouped = items.reduce((acc, item) => {
    (acc[item.date] = acc[item.date] || []).push(item);
    return acc;
  }, {});
  const days = Object.keys(grouped).sort().slice(0, 5);

  return (
    <div className="min-h-screen bg-[#0b1020] px-10 py-8 text-white"
         style={{ fontFeatureSettings: '"ss01"' }} data-testid="church-screen">
      <header className="flex items-start justify-between border-b border-white/10 pb-6">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-indigo-300">This week at</p>
          <h1 className="mt-1 text-5xl font-semibold tracking-tight"
              data-testid="screen-church-name">
            {data?.church?.name || '—'}
          </h1>
        </div>
        <Clock />
      </header>

      <main className="mt-8 grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5" data-testid="screen-days">
          {days.length === 0 && (
            <p className="text-2xl text-white/50" data-testid="screen-empty">
              Nothing else on the calendar this week.
            </p>
          )}
          {days.map((day) => {
            const date = new Date(`${day}T00:00:00`);
            return (
              <section key={day} data-testid={`screen-day-${day}`}
                       className="rounded-2xl bg-white/[0.04] px-7 py-5 backdrop-blur-sm">
                <p className="text-sm uppercase tracking-[0.25em] text-indigo-300">
                  {day === today ? 'Today' : DAYS[date.getDay()]}
                  <span className="ml-3 text-white/40">
                    {date.toLocaleDateString([], { day: 'numeric', month: 'short' })}
                  </span>
                </p>
                <ul className="mt-3 space-y-2">
                  {grouped[day].map((item) => (
                    <li key={item.id} className="flex items-baseline gap-5">
                      <span className="w-24 shrink-0 text-3xl font-semibold tabular-nums text-indigo-200">
                        {item.all_day ? 'All day' : (item.starts_at || '').slice(11, 16)}
                      </span>
                      <span className="text-3xl font-medium">{item.title}</span>
                      {item.location && (
                        <span className="text-xl text-white/50">· {item.location}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>

        <aside className="flex flex-col items-center justify-start gap-4 rounded-2xl bg-white/[0.04] p-6 text-center">
          {subscribe?.https_url ? (
            <>
              <div className="rounded-xl bg-white p-3" data-testid="screen-qr">
                <QRCode value={subscribe.https_url} size={180} />
              </div>
              <p className="text-base leading-snug text-white/70">
                Point your camera here to keep this calendar on your own phone —
                new services appear on their own.
              </p>
            </>
          ) : (
            <p className="text-white/40">—</p>
          )}
        </aside>
      </main>
    </div>
  );
}
